import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore'; // Importamos onSnapshot
// IMPORTAMOS EL COMPONENTE SEGURO
import SensitiveDataRow from '../../../ui/SensitiveDataRow';
// IMPORTAMOS LA UTILIDAD DE ESTILOS
import { getClasificacionChip } from '../../../../utils/styles';

const DatosContacto = ({ client, onEditClick }) => {
  const [contadorFull, setContadorFull] = useState(null);
  const [loadingContador, setLoadingContador] = useState(false);

  // --- EFFECT: ESCUCHAR DATOS EXTENDIDOS DEL CONTADOR EN TIEMPO REAL ---
  useEffect(() => {
    let unsubscribe = () => {};

    if (client?.tieneContador && client?.contadorId) {
      setLoadingContador(true);
      
      try {
        const docRef = doc(db, 'public/data/contadores', client.contadorId);
        
        // Implementamos el listener en tiempo real
        unsubscribe = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            setContadorFull({ id: docSnap.id, ...docSnap.data() });
          } else {
            setContadorFull(null);
          }
          setLoadingContador(false);
        }, (error) => {
          console.error("Error al obtener datos del contador en tiempo real:", error);
          setLoadingContador(false);
        });
      } catch (error) {
        console.error("Error al inicializar el listener del contador:", error);
        setLoadingContador(false);
      }
    } else {
      setContadorFull(null);
      setLoadingContador(false);
    }

    // Limpiamos la suscripción al desmontar el componente
    return () => unsubscribe();
  }, [client?.contadorId, client?.tieneContador]);

  if (!client) return <div className="p-10 text-center text-gray-400 italic text-left">Cargando datos del cliente...</div>;

  // --- HELPERS ---
  
  /**
   * formatPhoneNumber - Devuelve el valor plano (string) tal cual viene.
   */
  const formatPhoneNumber = (value) => {
    return value || '';
  };

  const DetailItem = ({ label, value }) => (
    <div className="text-left">
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider text-left">{label}</p>
      <p className="text-base text-gray-800 font-medium mt-1 min-h-[1.5rem] text-left uppercase">{value || '-'}</p>
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}-${month}-${year}`;
    }
    return dateString;
  };

  // --- LÓGICA DE PRIORIDAD Y COEXISTENCIA ---
  
  // 1. Procesamos los EMAILS
  const validEmailsArray = (Array.isArray(client.emails) ? client.emails : [])
    .filter(e => e && typeof e === 'string' && e.trim() !== '');

  // Prioridad: Si el array tiene datos, usamos el array. Si no, usamos el campo plano.
  const displayEmails = validEmailsArray.length > 0 
    ? validEmailsArray 
    : (client.email ? [client.email] : []);

  // 2. Procesamos los TELÉFONOS
  const validTelefonosArray = (Array.isArray(client.telefonos) ? client.telefonos : [])
    .filter(t => t && typeof t === 'string' && t.trim() !== '');

  // Prioridad: Si el array tiene datos, usamos el array. Si no, usamos el campo plano.
  const displayTelefonos = validTelefonosArray.length > 0 
    ? validTelefonosArray 
    : (client.telefono ? [client.telefono] : []);

  return (
    <div className="space-y-6 animate-fade-in text-left">
      
      {/* Botón Flotante de Edición */}
      <div className="flex justify-end">
        <button 
            onClick={onEditClick}
            className="flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 px-4 py-2 rounded-full hover:bg-blue-100 transition-colors shadow-sm border border-blue-100">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>
            Editar Cliente
        </button>
      </div>

      {/* TARJETA 1: DATOS PERSONALES */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-left">
        <div className="flex items-center mb-6 pb-2 border-b border-gray-100 text-left">
            <div className="p-2 bg-blue-100 rounded-lg text-blue-600 mr-3 text-left">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-left">Datos Personales</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
            <DetailItem label="Nombre / Razón Social" value={client.nombre} />
            <DetailItem label="Apellido" value={client.apellido} />
            <DetailItem label="DNI" value={client.dni} />
            <DetailItem label="CUIT" value={client.cuit} />
            <DetailItem label="Fecha de Nacimiento" value={formatDate(client.fechaNacimiento)} />
            <DetailItem label="Condición Fiscal" value={client.condicionFiscal} />
            <DetailItem label="Persona" value={client.persona} />
            
            <div className="text-left">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider text-left">Clasificación</p>
              <div className="mt-1 min-h-[1.5rem] flex items-center text-left">
                {client.clasificacion ? (
                    <span className={getClasificacionChip(client.clasificacion)}>
                        {client.clasificacion}
                    </span>
                ) : null}
              </div>
            </div>
        </div>
      </div>

      {/* TARJETA 2: ESTRUCTURA COMERCIAL */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-left">
        <div className="flex items-center mb-6 pb-2 border-b border-gray-100 text-left">
            <div className="p-2 bg-orange-100 rounded-lg text-orange-600 mr-3 text-left">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5" /></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-left">Estructura Comercial</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left mb-6">
            <DetailItem label="Tipo de Canal" value={client.canal} />
            <DetailItem label="Nombre del Canal" value={client.canalNombre} />
            <DetailItem label="Subcanal Asociado" value={client.subcanalNombre} />
            <DetailItem 
                label="¿Tiene Contador Asociado?" 
                value={client.tieneContador ? `SÍ (${client.contadorNombre || ''})` : 'NO'} 
            />
        </div>

        {client.tieneContador && (
          <div className={`transition-all duration-500 overflow-hidden ${loadingContador ? 'opacity-30 blur-[1px]' : 'opacity-100 blur-0'} text-left`}>
            {contadorFull?.asociados && contadorFull.asociados.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-50 animate-fade-in text-left">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2 text-left">
                  <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                  Contactos del Estudio Contable
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-left">
                  {contadorFull.asociados.map((asoc, idx) => (
                    <div key={idx} className="bg-gray-50/80 p-4 rounded-xl border border-gray-200 hover:border-orange-200 transition-colors text-left">
                      <div className="flex items-start justify-between text-left">
                        <div className="text-left">
                          <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2 text-left">
                            {asoc.nombre} {asoc.apellido}
                            {asoc.cargo && (
                              <span className="bg-orange-100 text-orange-700 text-[10px] uppercase px-2 py-0.5 rounded-full font-extrabold tracking-wide">
                                {asoc.cargo}
                              </span>
                            )}
                          </h4>
                          <div className="mt-2 space-y-1 text-left">
                            {asoc.email && (
                              <p className="text-sm text-gray-600 flex items-center text-left">
                                <svg className="w-3 h-3 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                <a href={`mailto:${asoc.email.toLowerCase()}`} className="hover:text-orange-600 hover:underline">{asoc.email.toLowerCase()}</a>
                              </p>
                            )}
                            {asoc.telefono && (
                              <p className="text-sm text-gray-600 flex items-center text-left">
                                <svg className="w-3 h-3 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                <a href={`tel:${asoc.telefono}`} className="hover:text-orange-600 hover:underline">
                                  {formatPhoneNumber(asoc.telefono)}
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* TARJETA 3: CONTACTO - CON PRIORIDAD DE ARRAYS SOBRE CAMPOS LEGACY */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-left">
        <div className="flex items-center mb-6 pb-2 border-b border-gray-100 text-left">
            <div className="p-2 bg-green-100 rounded-lg text-green-600 mr-3 text-left">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-left">Información de Contacto</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
            <div className="text-left">
                <p className="text-xs font-bold text-gray-400 uppercase mb-3 text-left">Correos Electrónicos</p>
                {displayEmails.length > 0 ? (
                    <ul className="space-y-2 text-left">
                        {displayEmails.map((e, idx) => (
                            <li key={idx} className="flex items-center text-gray-700 text-left">
                                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                                <a href={`mailto:${e.toLowerCase()}`} className="hover:text-blue-600 hover:underline font-medium lowercase">{e.toLowerCase()}</a>
                            </li>
                        ))}
                    </ul>
                ) : <p className="text-gray-400 italic font-light text-sm text-left">Sin datos</p>}
            </div>

            <div className="text-left">
                <p className="text-xs font-bold text-gray-400 uppercase mb-3 text-left">Teléfonos</p>
                {displayTelefonos.length > 0 ? (
                    <ul className="space-y-2 text-left">
                        {displayTelefonos.map((t, idx) => (
                            <li key={idx} className="flex items-center text-gray-700 text-left">
                                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                                <a href={`tel:${t}`} className="hover:text-green-600 hover:underline font-medium">
                                    {formatPhoneNumber(t)}
                                </a>
                            </li>
                        ))}
                    </ul>
                ) : <p className="text-gray-400 italic font-light text-sm text-left">Sin datos</p>}
            </div>
        </div>
      </div>

      {/* TARJETA 4: CONTACTO ADICIONAL */}
      {client.contactosAdicionales && client.contactosAdicionales.length > 0 && (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm animate-fade-in text-left">
            <div className="flex items-center mb-6 pb-2 border-b border-gray-100 text-left">
                <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600 mr-3 text-left">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                </div>
                <h3 className="text-lg font-bold text-gray-800 text-left">Contactos Adicionales</h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-left">
                {client.contactosAdicionales.map((contacto, idx) => (
                    <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-left">
                        <div className="flex items-start justify-between text-left">
                            <div className="text-left">
                                <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2 text-left">
                                    {contacto.nombre} {contacto.apellido}
                                    {contacto.cargo && (
                                        <span className="bg-indigo-100 text-indigo-700 text-[10px] uppercase px-2 py-0.5 rounded-full font-extrabold tracking-wide">
                                            {contacto.cargo}
                                        </span>
                                    )}
                                </h4>
                                <div className="mt-2 space-y-1 text-left">
                                    {contacto.email && (
                                        <p className="text-sm text-gray-600 flex items-center text-left">
                                            <svg className="w-3 h-3 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                            <a href={`mailto:${contacto.email.toLowerCase()}`} className="hover:text-indigo-600 hover:underline lowercase">{contacto.email.toLowerCase()}</a>
                                        </p>
                                    )}
                                    {contacto.telefono && (
                                        <p className="text-sm text-gray-600 flex items-center text-left">
                                            <svg className="w-3 h-3 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                            <a href={`tel:${contacto.telefono}`} className="hover:text-indigo-600 hover:underline">
                                                {formatPhoneNumber(contacto.telefono)}
                                            </a>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      )}

      {/* TARJETA 5: DOMICILIO */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-left">
         <div className="flex items-center mb-6 pb-2 border-b border-gray-100 text-left">
            <div className="p-2 bg-purple-100 rounded-lg text-purple-600 mr-3 text-left">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-left">Domicilio</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-left">
            <div className="col-span-2 text-left"><DetailItem label="Calle / Dirección" value={client.domicilio} /></div>
            <DetailItem label="Número" value={client.domicilio_numero} />
            <DetailItem label="Piso/Depto" value={client.piso || client.departamento ? `${client.piso || ''} ${client.departamento || ''}` : ''} />
            <DetailItem label="Localidad" value={client.localidad} />
            <DetailItem label="Provincia" value={client.provincia} />
            <DetailItem label="C.P." value={client.codigoPostal} />
        </div>
      </div>

      {/* TARJETA 6: DATOS BANCARIOS */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-left">
        <div className="flex items-center mb-6 pb-2 border-b border-gray-100 text-left">
            <div className="p-2 bg-orange-100 rounded-lg text-orange-600 mr-3 text-left">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-left">Datos Bancarios</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
            <div className="text-left">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider text-left mb-4">Tarjetas Registradas</p>
                {client.tarjetas && client.tarjetas.length > 0 ? (
                    <div className="space-y-4 text-left">
                        {client.tarjetas.map((t, idx) => {
                            const numFinal = t.numeroTarjeta || t.numero;
                            const vtoFinal = t.vencimientoTarjeta || t.vencimiento;
                            return (
                                <div key={idx} className="bg-slate-50/50 border border-slate-200 rounded-2xl p-4 hover:border-blue-300 transition-all shadow-sm text-left">
                                    <div className="flex items-center gap-3 mb-3 text-left">
                                        <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg text-left">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                                        </div>
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-tight text-left">{t.marca || 'Tarjeta'} - {t.banco || 'Banco'}</span>
                                    </div>
                                    <SensitiveDataRow label="Número de Tarjeta" value={numFinal} type="card" originalValue={numFinal} showLastFourOnly={true} />
                                    <div className="mt-2 pl-1.5 flex items-center gap-2 text-left">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest text-left">Vencimiento:</span>
                                        <span className="text-xs font-bold text-slate-600 bg-white px-2 py-0.5 rounded border border-slate-100 shadow-sm text-left">{vtoFinal || 'S/V'}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : <p className="text-gray-400 italic font-light text-sm text-left">Sin tarjetas registradas.</p>}
            </div>

            <div className="text-left">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider text-left mb-4">Cuentas Bancarias (CBU)</p>
                {client.cbus && client.cbus.length > 0 ? (
                    <div className="space-y-4 text-left">
                        {client.cbus.map((cbu, idx) => {
                            const cbuFinal = cbu.numeroCbu || cbu.cbu || cbu.numero;
                            return (
                                <div key={idx} className="bg-emerald-50/20 border border-emerald-100 rounded-2xl p-4 hover:border-emerald-300 transition-all shadow-sm text-left">
                                    <div className="flex items-center gap-3 mb-3 text-left">
                                        <div className="p-1.5 bg-emerald-100 text-emerald-600 rounded-lg text-left">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                                        </div>
                                        <span className="text-xs font-black text-emerald-800 uppercase tracking-tight text-left">{cbu.banco || 'BANCO'} {cbu.alias ? `(${cbu.alias})` : ''}</span>
                                    </div>
                                    <SensitiveDataRow label="Número de CBU" value={cbuFinal} type="cbu" originalValue={cbuFinal} showLastFourOnly={true} />
                                </div>
                            );
                        })}
                    </div>
                ) : <p className="text-gray-400 italic font-light text-sm text-left">Sin CBUs registrados.</p>}
            </div>
        </div>
      </div>

      {/* TARJETA 7: OBSERVACIONES */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-left">
         <div className="flex items-center mb-6 pb-2 border-b border-gray-100 text-left">
            <div className="p-2 bg-yellow-100 rounded-lg text-yellow-600 mr-3 text-left">
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-left">Observaciones</h3>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 text-left">
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed min-h-[3rem] text-left">
                {client.observaciones || 'Sin observaciones'}
            </p>
        </div>
      </div>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default DatosContacto;